import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LockKey, ArrowRight } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { api, formatErr } from "@/lib/api";

export const GATE_STORAGE_KEY = "hunt57_gate_ok";

export default function SiteGate({ onPass }) {
  const [code, setCode] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [loading, setLoading] = useState(false);

  const isPreview = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("preview_gate") === "1";

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = code.trim();
    if (!cleaned) {
      toast.error("Enter an access code");
      return;
    }
    setLoading(true);
    try {
      await api.post("/access/verify", { code: cleaned });
      if (!isPreview) {
        localStorage.setItem(GATE_STORAGE_KEY, "1");
        toast.success("Welcome to Hunt57");
        onPass?.();
      } else {
        toast.success("Code accepted (preview mode — flag not stored)");
      }
    } catch (err) {
      toast.error(formatErr(err) || "Invalid access code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen text-white font-body flex flex-col h57-grid-bg"
      data-testid="site-gate"
    >
      <main className="relative flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl text-center">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#FF5F1F] mb-8" data-testid="gate-eyebrow">
            → Site Under Development
          </div>

          {isPreview && (
            <div className="mb-6 inline-block px-3 py-1 border border-[#FFD700]/60 text-[#FFD700] font-mono text-[10px] uppercase tracking-widest" data-testid="gate-preview-badge">
              Preview Mode
            </div>
          )}

          <div className="flex justify-center mb-8" data-testid="gate-logo">
            <Logo variant="hero" alt="Hunt57" className="max-h-40 md:max-h-52" />
          </div>

          <h1
            className="font-display font-black tracking-tighter leading-[0.85] text-5xl sm:text-6xl md:text-7xl uppercase"
            data-testid="gate-title"
          >
            Hunt. Fetch. <span className="text-[#FF5F1F]">Collect.</span>
          </h1>

          <p className="mt-8 text-base md:text-lg text-zinc-300 leading-relaxed" data-testid="gate-desc">
            The <span className="text-white font-semibold">private card marketplace</span> — invite-only vendors, zero seller fees.{" "}
            Buyers browse freely; vendors apply for review or fast-track with a referral voucher.
          </p>

          <div className="mt-10 font-mono text-[11px] text-zinc-500 tracking-widest uppercase">
            Coming Soon
          </div>

          <div className="mt-8">
            {!showInput ? (
              <button
                onClick={() => setShowInput(true)}
                data-testid="gate-open-input"
                className="h57-btn-primary inline-flex items-center gap-2"
              >
                <LockKey size={18} weight="bold" />
                Access Site
              </button>
            ) : (
              <form onSubmit={submit} className="mx-auto max-w-sm space-y-3" data-testid="gate-form">
                <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-left">
                  Access Code
                </label>
                <input
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="HUNT-XXXXXX"
                  data-testid="gate-code-input"
                  className="h57-input text-center tracking-[0.2em] font-mono uppercase"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="gate-submit"
                  className="h57-btn-primary w-full inline-flex items-center justify-center gap-2"
                >
                  {loading ? "Verifying…" : (<>Enter Site <ArrowRight size={16} weight="bold" /></>)}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowInput(false); setCode(""); }}
                  data-testid="gate-cancel"
                  className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
                >
                  Cancel
                </button>
              </form>
            )}
          </div>

          <div className="mt-16 pt-8 border-t border-white/5 font-mono text-[10px] text-zinc-600 tracking-widest uppercase">
            Already a member?{" "}
            <Link
              to="/login"
              onClick={() => localStorage.setItem(GATE_STORAGE_KEY, "1")}
              className="text-zinc-300 hover:text-[#FF5F1F] underline underline-offset-4"
              data-testid="gate-signin-link"
            >
              Sign In
            </Link>
          </div>
        </div>
      </main>

      <footer className="relative py-6 text-center font-mono text-[10px] text-zinc-600 tracking-widest uppercase">
        © Hunt57 · Hunt. Fetch. Collect.
      </footer>
    </div>
  );
}
